import * as React from 'react';
import {useEffect, useMemo, useRef, useState, useCallback} from 'react';
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import ChatBubble from './ChatBubble';
import MessagesPaneHeader from './MessagesPaneHeader';
import {CircularProgress} from "@mui/joy";
import {toast} from 'react-toastify';
import Typography from "@mui/joy/Typography";
import BlockIcon from '@mui/icons-material/Block';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import GeneralApiService, {GetMessagesForChat, GetWhatsAppMessagesForChat, GetUser, PostSMSTextResponse, PostWhatsAppResponse, UpdateEmailReadStatus} from "../../../axios/Axios";
import BookingsDrawer from "../../modals/BookingsDrawer";
import LeadsDrawer from "../../modals/lead/LeadsDrawer";
import EmailThread from './EmailThread';
import RonaldStatusStrip, {RonaldStatus, RonaldTask, RonaldPendingBubble} from './RonaldStatusStrip';
import RonaldAuditCard from './RonaldAuditCard';
import RonaldFutureMessageCard from './RonaldFutureMessageCard';
import type {ChatProps, ChannelType} from './Messages';

type MessagesPaneProps = {
    chat: ChatProps | null;
    activeChannel: ChannelType;
    onChannelChange: (channel: ChannelType) => void;
    onOptOut?: () => void;
    onMarkChannelRead?: (channel: 'sms' | 'whatsapp' | 'email', chatId: string) => void;
    /** When true (e.g. inside lead/booking drawer), apply alignment padding so tabs and input match message area. */
    inDrawer?: boolean;
    /** Called when the mobile back arrow is pressed to return to the chat list. */
    onBack?: () => void;
    /** Called when the outbound message count changes so the sidebar can refresh its preview snippet. */
    onRefreshChatList?: () => void;
};
interface User {
    username: string;
}

export default function MessagesPane(props: MessagesPaneProps) {
    const {chat, activeChannel, onChannelChange, onOptOut, onMarkChannelRead, inDrawer, onBack, onRefreshChatList} = props;
    const [chatMessages, setChatMessages] = useState<MessageProps[]>([]);
    const [textAreaValue, setTextAreaValue] = useState('');
    const [user, setUser] = useState<User>({username: ''});
    const [ronaldStatus, setRonaldStatus] = useState<RonaldStatus | null>(null);
    const [ronaldRefreshKey, setRonaldRefreshKey] = useState(0);
    const prevMessageCountRef = useRef(0);
    const prevInterventionRef = useRef<boolean | undefined>(undefined);
    const prevPendingCountRef = useRef<number | undefined>(undefined);
    const refreshTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isInitialLoading, setIsInitialLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [canRespond, setCanRespond] = useState(true);

    const [lead, setLead] = React.useState<Lead | null>(null);
    const [leadValue, setLeadValue] = React.useState<ServiceValue | null>(null);

    const [openBookingModal, setOpenBookingModal] = React.useState(false);
    const [openBookingId, setOpenBookingId] = React.useState(0);
    const [openLeadModal, setOpenLeadModal] = React.useState(false);
    const [openLeadId, setOpenLeadId] = React.useState(0);

    // Track which SMS/WhatsApp channel was last loaded to avoid unnecessary refetches
    const [loadedChannel, setLoadedChannel] = useState<string | null>(null);

    const type = activeChannel === 'sms' ? 'sms' : activeChannel === 'whatsapp' ? 'whatsapp' : null;

    const fetchMessages = useCallback(async (chatId: string, pageNum: number, channel: 'sms' | 'whatsapp') => {
        if (pageNum === 1) setIsInitialLoading(true);
        else setIsLoadingMore(true);

        try {
            const userResponse = await GetUser();
            const response = channel === 'sms'
                ? await GetMessagesForChat(chatId, pageNum)
                : await GetWhatsAppMessagesForChat(chatId, pageNum);

            const {messages: newMessages, hasMore: apiHasMore, latestLead, latestLeadValue, can_respond} = response.data;

            if (channel === 'whatsapp') {
                setCanRespond(can_respond);
            } else {
                setCanRespond(true);
            }

            setLead(latestLead || null);
            setLeadValue(latestLeadValue || null);

            setChatMessages(prev => pageNum === 1 ? newMessages : [...newMessages, ...prev]);
            setHasMore(apiHasMore);
            setUser(userResponse.data);
        } catch (error) {
            console.error("Failed to fetch messages:", error);
        } finally {
            setIsInitialLoading(false);
            setIsLoadingMore(false);
        }
    }, []);

    const refreshChat = useCallback(() => {
        if (chat?.id && type) {
            setChatMessages([]);
            setPage(1);
            setHasMore(true);
            setLoadedChannel(null); // Force refetch
            fetchMessages(chat.id, 1, type);
            setLoadedChannel(`${chat.id}-${type}`);
        }
    }, [chat?.id, type, fetchMessages]);

    useEffect(() => {
        return () => { refreshTimersRef.current.forEach(clearTimeout); };
    }, []);

    // Only fetch SMS/WhatsApp when chat or the SMS/WhatsApp channel actually changes
    useEffect(() => {
        if (!chat?.id || !type) return;

        const channelKey = `${chat.id}-${type}`;
        if (loadedChannel === channelKey) return; // Already loaded this channel for this chat

        setChatMessages([]);
        setPage(1);
        setHasMore(true);
        fetchMessages(chat.id, 1, type);
        setLoadedChannel(channelKey);

        // Mark channel as read after loading
        if (onMarkChannelRead) {
            if (type === 'sms' && chat.unread_sms) onMarkChannelRead('sms', chat.id);
            if (type === 'whatsapp' && chat.unread_whatsapp) onMarkChannelRead('whatsapp', chat.id);
        }
    }, [chat?.id, type]);

    // Reset loaded channel when chat changes so all tabs refetch fresh data
    useEffect(() => {
        setLoadedChannel(null);
        prevMessageCountRef.current = 0;
        setRonaldStatus(null);
    }, [chat?.id]);

    // When the message count grows (Ronald or someone else added a message during polling),
    // nudge the parent so the sidebar snippet updates.
    useEffect(() => {
        const prev = prevMessageCountRef.current;
        if (chatMessages.length > prev && prev > 0 && onRefreshChatList) {
            onRefreshChatList();
        }
        prevMessageCountRef.current = chatMessages.length;
    }, [chatMessages.length, onRefreshChatList]);

    // When Ronald's intervention status changes (via polling), refresh the
    // chat list so the amber warning icon in the sidebar updates in real time.
    useEffect(() => {
        const current = ronaldStatus?.needs_human_intervention;
        if (prevInterventionRef.current !== undefined && current !== prevInterventionRef.current && onRefreshChatList) {
            onRefreshChatList();
        }
        prevInterventionRef.current = current;
    }, [ronaldStatus?.needs_human_intervention, onRefreshChatList]);

    // When a pending task disappears (Ronald sent the message or it was
    // cancelled/intervened), refresh messages, sidebar, and Ronald status
    // so the sent message and audit cards appear immediately.
    const pendingCount = ronaldStatus?.pending_tasks?.length ?? 0;
    useEffect(() => {
        const prev = prevPendingCountRef.current;
        if (prev !== undefined && pendingCount < prev) {
            refreshChat();
            if (onRefreshChatList) onRefreshChatList();
            setRonaldRefreshKey(k => k + 1);
        }
        prevPendingCountRef.current = pendingCount;
    }, [pendingCount, refreshChat, onRefreshChatList]);

    const handleLoadMore = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        if (chat?.id && type) {
            fetchMessages(chat.id, nextPage, type);
        }
    };

    const handleSendMessage = async () => {
        if (!textAreaValue.trim() || !chat || !user.username || !type) return;

        const optimisticMessage: any = {
            id: `temp-${Date.now()}`,
            sender: {id: '-1', name: user.username, phone: chat.sender.phone, isCustomer: false, username: user.username},
            content: textAreaValue,
            timestamp: new Date().toISOString(),
            unread: false,
            customer_id: chat.id,
        };
        setChatMessages(prev => [...prev, optimisticMessage]);
        setTextAreaValue('');

        try {
            let response;
            if (type === 'sms') {
                response = await PostSMSTextResponse(optimisticMessage);
            } else {
                response = await PostWhatsAppResponse(optimisticMessage);
            }

            if (response?.data?.reason === 'unsubscribed') {
                toast.error(response.data.message || 'This customer has opted out and cannot receive messages');
                setChatMessages(prev => prev.filter(m => m.id !== optimisticMessage.id));
            }
        } catch (error) {
            console.error("Failed to send message", error);
            setChatMessages(prev => prev.filter(m => m.id !== optimisticMessage.id));
        }
    };

    const handleCloseBookingModal = (updateInfo?: { id: number }) => {
        setOpenBookingModal(false);
        if (updateInfo) refreshChat();
    };

    const handleCloseLeadModal = (updateInfo?: { id: number }) => {
        setOpenLeadModal(false);
        if (updateInfo) refreshChat();
    };

    // Build a merged timeline: chat messages + cancelled / intervened Ronald
    // task audit pills + pending follow-up "Future message" cards, sorted by
    // timestamp so staff see everything Ronald has done (and will do) inline.
    // Declared before the early-return so the useMemo hook order is stable.
    const cancelledTasksRaw = ronaldStatus?.cancelled_tasks;
    const pendingTasksRaw = ronaldStatus?.pending_tasks;
    const timelineItems = useMemo(() => {
        type TimelineItem =
            | { kind: 'message'; key: string; timestamp: number; message: MessageProps }
            | { kind: 'audit'; key: string; timestamp: number; task: RonaldTask }
            | { kind: 'future'; key: string; timestamp: number; task: RonaldTask };
        const items: TimelineItem[] = [];
        chatMessages.forEach((m: MessageProps, idx: number) => {
            const ts = m?.timestamp ? new Date(m.timestamp).getTime() : idx;
            items.push({
                kind: 'message',
                key: String(m.id ?? idx),
                timestamp: Number.isFinite(ts) ? ts : idx,
                message: m,
            });
        });
        (cancelledTasksRaw || []).forEach((task: RonaldTask) => {
            const raw = task.cancelled_at || task.updated_at || task.created_at;
            const ts = raw ? new Date(raw).getTime() : 0;
            items.push({
                kind: 'audit',
                key: `audit-${task.id}`,
                timestamp: Number.isFinite(ts) ? ts : 0,
                task,
            });
        });
        // Pending FOLLOW_UP tasks render as "Future message" cards anchored to
        // their scheduled_at so they sort naturally to the bottom (in the future).
        (pendingTasksRaw || [])
            .filter((t: RonaldTask) => t.task_type === 'FOLLOW_UP')
            .forEach((task: RonaldTask) => {
                const raw = task.scheduled_at || task.created_at;
                const ts = raw ? new Date(raw).getTime() : Date.now();
                items.push({
                    kind: 'future',
                    key: `future-${task.id}`,
                    timestamp: Number.isFinite(ts) ? ts : Date.now(),
                    task,
                });
            });
        items.sort((a, b) => a.timestamp - b.timestamp);
        return items;
    }, [chatMessages, cancelledTasksRaw, pendingTasksRaw]);

    // Hooks for the Ronald controls rendered in the header + composer row.
    // Declared before the early-return so hook order stays stable.
    const [askingRonald, setAskingRonald] = useState(false);
    const [togglingRonaldAi, setTogglingRonaldAi] = useState(false);
    const [busyTaskId, setBusyTaskId] = useState<number | null>(null);
    const pendingReplyTask = useMemo(() => (
        (ronaldStatus?.pending_tasks || []).find(
            (t: RonaldTask) => t.task_type === 'MESSAGE_REPLY' || t.task_type === 'SEQUENCE_STEP'
        )
    ), [ronaldStatus]);
    const handleAskRonald = useCallback(async () => {
        if (!chat?.id) return;
        setAskingRonald(true);
        // Optimistically inject a fake PENDING task so the "Thinking"
        // bubble appears instantly — no waiting for the next poll.
        const optimisticTask: RonaldTask = {
            id: -Date.now(),
            status: 'PENDING',
            task_type: 'MESSAGE_REPLY',
            channel: 'SMS',
            needs_human_intervention: false,
            intervention_acknowledged: false,
            created_at: new Date().toISOString(),
        };
        setRonaldStatus(prev => {
            if (!prev) return prev;
            return {...prev, pending_tasks: [...(prev.pending_tasks || []), optimisticTask]};
        });
        try {
            await (GeneralApiService as any).triggerRonaldReply(chat.id);
        } catch {
            // Roll back the optimistic task on failure.
            setRonaldStatus(prev => {
                if (!prev) return prev;
                return {...prev, pending_tasks: (prev.pending_tasks || []).filter(t => t.id !== optimisticTask.id)};
            });
        } finally {
            setAskingRonald(false);
        }
    }, [chat?.id]);
    const handleToggleRonaldAi = useCallback(async (nextEnabled: boolean) => {
        if (!chat?.id) return;
        setTogglingRonaldAi(true);
        setRonaldStatus(prev => (prev ? {...prev, ai_responses_enabled: nextEnabled} : prev));
        try {
            await (GeneralApiService as any).toggleRonaldAiForCustomer(chat.id, nextEnabled);
        } catch {
            setRonaldStatus(prev => (prev ? {...prev, ai_responses_enabled: !nextEnabled} : prev));
        } finally {
            setTogglingRonaldAi(false);
        }
    }, [chat?.id]);
    const cancellingRef = useRef<Set<number>>(new Set());
    const handleCancelRonaldTask = useCallback(async (taskId: number) => {
        if (cancellingRef.current.has(taskId)) return;
        cancellingRef.current.add(taskId);
        setBusyTaskId(taskId);
        setRonaldStatus(prev => (
            prev
                ? {...prev, pending_tasks: (prev.pending_tasks || []).filter(t => t.id !== taskId)}
                : prev
        ));
        try {
            if (taskId > 0) {
                await (GeneralApiService as any).cancelRonaldTask(taskId, 'Cancelled from messages page');
            }
            const t = setTimeout(() => {
                setRonaldRefreshKey(k => k + 1);
                refreshChat();
            }, 500);
            refreshTimersRef.current.push(t);
        } catch {
            // Polling will reconcile.
        } finally {
            cancellingRef.current.delete(taskId);
            setBusyTaskId(null);
        }
    }, [refreshChat]);
    const sendingRef = useRef<Set<number>>(new Set());
    const handleSendNowRonaldTask = useCallback(async (taskId: number) => {
        if (taskId < 0) return;
        if (sendingRef.current.has(taskId)) return;
        sendingRef.current.add(taskId);
        setBusyTaskId(taskId);
        setRonaldStatus(prev => (
            prev
                ? {...prev, pending_tasks: (prev.pending_tasks || []).filter(t => t.id !== taskId)}
                : prev
        ));
        try {
            await (GeneralApiService as any).forceExecuteRonaldTask(taskId);
            const t = setTimeout(() => {
                setRonaldRefreshKey(k => k + 1);
                refreshChat();
                if (onRefreshChatList) onRefreshChatList();
            }, 500);
            refreshTimersRef.current.push(t);
        } catch {
            // Polling will reconcile.
        } finally {
            sendingRef.current.delete(taskId);
            setBusyTaskId(null);
        }
    }, [refreshChat, onRefreshChatList]);

    if (!chat) {
        return (
            <Box sx={{height: '100%', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, color: '#9ca3af'}}>
                <Typography level="body-md" sx={{color: '#9ca3af'}}>Select a conversation to start messaging</Typography>
                <Typography level="body-xs" sx={{color: '#9ca3af'}}>Incoming messages will appear in the sidebar automatically.</Typography>
            </Box>
        );
    }

    const hasSms = chat.has_sms ?? false;
    const hasWhatsApp = chat.has_whatsapp ?? false;
    const hasEmail = chat.has_email ?? false;
    const unreadSms = chat.unread_sms ?? false;
    const unreadWhatsApp = chat.unread_whatsapp ?? false;
    const unreadEmail = chat.unread_email ?? false;

    const isEmailActive = activeChannel === 'email';
    const isSmsWhatsAppActive = !isEmailActive;

    // Opt-out resolves from the Ronald status endpoint (customer_details.opt_out)
    // or the chat sender flag we may already have. When true we block the input
    // area, surface a banner, and hide the Ask-Ronald affordance.
    const isOptedOut = !!(
        ronaldStatus?.customer_details?.opt_out ||
        ronaldStatus?.customer_details?.unsubscribed ||
        (chat as any)?.sender?.unsubscribed
    );

    // "Ask Ronald to reply" — shown in the composer row above the textarea
    // (next to the channel chips). Hooks (askingRonald / pendingReplyTask /
    // handleAskRonald) live above the early-return so hook order stays stable;
    // this is the plain derived visibility flag that uses them.
    const canAskRonald = !!(
        ronaldStatus &&
        ronaldStatus.scope !== 'none' &&
        !isOptedOut
    );

    // Main content padding: full width in drawer except message area; standard padding on Messages page
    const contentPaddingX = inDrawer ? 0 : 2;
    // Message list keeps padding so chat bubbles have breathing room (in drawer and on Messages page)
    const messageAreaPaddingX = 2;

    return (
        <Box
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: '#f9fafb',
                minHeight: 0,
                overflow: 'hidden',
                minWidth: 0,
            }}
        >
            <React.Fragment>
                {openBookingModal && <BookingsDrawer key={`bookings-modal-${openBookingId}`} openState={openBookingModal} newBooking={null} newBookingValue={null} bookingId={openBookingId} parentCallback={handleCloseBookingModal}/>}
                {openLeadModal && <LeadsDrawer key={`leads-modal-${openLeadId}`} openState={openLeadModal} leadId={openLeadId} parentCallback={handleCloseLeadModal}/>}
            </React.Fragment>

            <MessagesPaneHeader
                sender={chat.sender}
                category={chat.call_category}
                onOptOut={onOptOut}
                contentPaddingX={inDrawer ? contentPaddingX : undefined}
                onBack={onBack}
                ronaldScope={ronaldStatus?.scope}
                ronaldAiEnabled={ronaldStatus?.ai_responses_enabled}
                onToggleRonaldAi={handleToggleRonaldAi}
                togglingRonaldAi={togglingRonaldAi}
                latestBookingId={chat.latest_booking_id}
                latestBookingStatus={chat.latest_booking_status}
                latestBookingBookedIn={chat.latest_booking_booked_in}
            />

            {/* Invisible polling engine — pushes Ronald status to the
                parent; all visual elements are rendered inline below. */}
            <RonaldStatusStrip
                customerId={chat.id}
                refreshKey={chatMessages.length + ronaldRefreshKey * 10000}
                onStatusChange={setRonaldStatus}
            />

            {/* Email tab content */}
            {isEmailActive && (
                <EmailThread
                    chatId={chat.id}
                    customerEmail={chat.sender?.email}
                    onMarkRead={() => {
                        if (onMarkChannelRead && chat.unread_email) {
                            onMarkChannelRead('email', chat.id);
                        }
                    }}
                />
            )}

            {/* SMS / WhatsApp tab content */}
            {isSmsWhatsAppActive && (
                <>
                    {isInitialLoading && chatMessages.length === 0 ? (
                        <Box sx={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                            <CircularProgress size="lg"/>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                flex: 1,
                                minHeight: 0,
                                px: 2,
                                py: 2,
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                flexDirection: 'column-reverse',
                                WebkitOverflowScrolling: 'touch',
                                bgcolor: '#f9fafb',
                            }}
                        >
                            <Stack spacing={2} sx={{flexDirection: 'column', minWidth: 0}}>
                                {timelineItems.map((item) => {
                                    if (item.kind === 'audit') {
                                        return <RonaldAuditCard key={item.key} task={item.task}/>;
                                    }
                                    if (item.kind === 'future') {
                                        return (
                                            <RonaldFutureMessageCard
                                                key={item.key}
                                                task={item.task}
                                                busy={busyTaskId === item.task.id}
                                                onCancel={handleCancelRonaldTask}
                                            />
                                        );
                                    }
                                    const message = item.message;
                                    const isYou = !message.sender.isCustomer;
                                    if (message.leadData || message.bookingData) {
                                        return <div key={item.key}/>;
                                    }
                                    return (
                                        <Stack key={item.key} direction="row" spacing={2} sx={{justifyContent: isYou ? 'flex-end' : 'flex-start'}}>
                                            <ChatBubble variant={isYou ? 'sent' : 'received'} {...message} channel={message.channel || (type === 'whatsapp' ? 'WhatsApp' : 'SMS')} />
                                        </Stack>
                                    );
                                })}

                                {/* Pending reply bubble (thinking / ready-to-send) */}
                                {pendingReplyTask && (
                                    <RonaldPendingBubble
                                        task={pendingReplyTask}
                                        busy={busyTaskId === pendingReplyTask.id}
                                        onSendNow={() => handleSendNowRonaldTask(pendingReplyTask.id)}
                                        onCancel={() => handleCancelRonaldTask(pendingReplyTask.id)}
                                    />
                                )}

                            </Stack>

                            {hasMore && (
                                <Box sx={{textAlign: 'center', my: 1}}>
                                    <Box
                                        component="button"
                                        onClick={handleLoadMore}
                                        disabled={isLoadingMore}
                                        sx={{
                                            border: 'none',
                                            bgcolor: 'transparent',
                                            color: '#2563eb',
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            cursor: isLoadingMore ? 'wait' : 'pointer',
                                            '&:hover': {textDecoration: 'underline'},
                                        }}
                                    >
                                        {isLoadingMore ? 'Loading...' : 'Load More Messages'}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                    {type === 'whatsapp' && !canRespond && !isOptedOut && (
                        <Box sx={{p: 2, bgcolor: '#fffbeb', borderTop: '1px solid', borderColor: '#fcd34d'}}>
                            <Typography level="body-sm" sx={{textAlign: 'center', fontWeight: 600, color: '#b45309'}}>
                                The 24-hour WhatsApp response window is closed. You can only send pre-approved templates.
                            </Typography>
                        </Box>
                    )}
                    {isOptedOut ? (
                        <Box
                            sx={{
                                p: 2,
                                bgcolor: '#fef2f2',
                                borderTop: '1px solid',
                                borderColor: '#fecaca',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                                flexShrink: 0,
                            }}
                        >
                            <BlockIcon sx={{fontSize: 16, color: '#dc2626'}}/>
                            <Typography level="body-sm" sx={{color: '#dc2626', fontWeight: 'md'}}>
                                This customer has opted out of communications
                            </Typography>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                p: 2,
                                bgcolor: '#fff',
                                borderTop: '1px solid',
                                borderColor: '#e5e7eb',
                                flexShrink: 0,
                            }}
                        >
                            {/* Channel pills row + Ask Ronald */}
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                {([
                                    {key: 'sms' as const, label: 'SMS', has: hasSms, alwaysEnabled: true},
                                    {key: 'whatsapp' as const, label: 'WhatsApp', has: hasWhatsApp, alwaysEnabled: false},
                                    {key: 'email' as const, label: 'Email', has: hasEmail, alwaysEnabled: false},
                                ] as const).filter(ch => ch.alwaysEnabled || ch.has).map(ch => {
                                    const enabled = ch.alwaysEnabled || ch.has;
                                    return (
                                        <Box
                                            key={ch.key}
                                            component="button"
                                            onClick={enabled ? () => onChannelChange(ch.key) : undefined}
                                            sx={{
                                                px: 1.5,
                                                py: 0.5,
                                                fontSize: '12px',
                                                fontWeight: 500,
                                                borderRadius: '9999px',
                                                border: '1px solid',
                                                cursor: enabled ? 'pointer' : 'not-allowed',
                                                opacity: enabled ? 1 : 0.4,
                                                transition: 'all 0.15s',
                                                ...(activeChannel === ch.key
                                                    ? {bgcolor: '#2563eb', color: '#fff', borderColor: '#2563eb'}
                                                    : {bgcolor: '#fff', color: '#4b5563', borderColor: '#e5e7eb', '&:hover': enabled ? {bgcolor: '#f3f4f6'} : {}}),
                                            }}
                                        >
                                            {ch.label}
                                        </Box>
                                    );
                                })}
                                {canAskRonald && type === 'sms' && (
                                    <Box
                                        component="button"
                                        onClick={askingRonald ? undefined : handleAskRonald}
                                        sx={{
                                            ml: 'auto',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                            borderRadius: '9999px',
                                            border: '1px solid',
                                            borderColor: '#e9d5ff',
                                            bgcolor: '#faf5ff',
                                            px: 1.5,
                                            py: 0.5,
                                            fontSize: '12px',
                                            fontWeight: 500,
                                            color: '#7c3aed',
                                            cursor: askingRonald ? 'wait' : 'pointer',
                                            transition: 'all 0.15s',
                                            '&:hover': {bgcolor: '#f3e8ff'},
                                        }}
                                    >
                                        <SmartToyRoundedIcon sx={{fontSize: 14}}/>
                                        {askingRonald ? 'Asking...' : 'Ask Ronald to reply'}
                                    </Box>
                                )}
                            </Box>

                            {/* Textarea + Send */}
                            <Box sx={{display: 'flex', gap: 1}}>
                                <Box
                                    component="textarea"
                                    value={textAreaValue}
                                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setTextAreaValue(e.target.value)}
                                    onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleSendMessage();
                                        }
                                    }}
                                    placeholder={`Type a ${type === 'sms' ? 'text message' : type === 'whatsapp' ? 'message' : 'email'}...`}
                                    disabled={type === 'whatsapp' && !canRespond}
                                    rows={3}
                                    sx={{
                                        flex: 1,
                                        border: '1px solid',
                                        borderColor: '#e5e7eb',
                                        borderRadius: '6px',
                                        p: 1,
                                        fontSize: '14px',
                                        fontFamily: 'inherit',
                                        resize: 'none',
                                        outline: 'none',
                                        '&:focus': {borderColor: '#3b82f6'},
                                    }}
                                />
                                <Box
                                    component="button"
                                    onClick={handleSendMessage}
                                    disabled={!textAreaValue.trim()}
                                    sx={{
                                        bgcolor: '#2563eb',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '6px',
                                        px: 2,
                                        fontWeight: 500,
                                        fontSize: '14px',
                                        cursor: !textAreaValue.trim() ? 'not-allowed' : 'pointer',
                                        opacity: !textAreaValue.trim() ? 0.5 : 1,
                                        transition: 'all 0.15s',
                                        '&:hover': textAreaValue.trim() ? {bgcolor: '#1d4ed8'} : {},
                                    }}
                                >
                                    Send
                                </Box>
                            </Box>
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
}
